// CSS import order matters for cascade specificity. This sequence
// matches the original style.scss order from the CRA build:
//   theme → node-module CSS → App.scss (which ends with custom overrides)
//   → tributejs/toastify (originally after custom fonts, before custom overrides,
//     but safe here because custom.scss selectors are specific enough)
//
// Vite requires node module CSS in JS imports (no ~ prefix in SCSS).
// Dashkit theme must be a JS import so Vite resolves font url() paths
// relative to the actual CSS file location.
import './vendor/good-themes/dashkit/css/theme.css';
import 'react-image-gallery/styles/scss/image-gallery.scss';
import 'react-image-gallery/styles/css/image-gallery.css';
import 'spinkit/spinkit.min.css';
import 'tributejs/tribute.css';
import 'react-toastify/dist/ReactToastify.css';
// App.scss loads last so custom.scss and react-tags-custom.scss
// can override all vendor styles above.
import './App.scss';
// import Chart.js defaults from Dashkit
import './vendor/good-themes/dashkit/js/charts';

import { showSupportChat } from 'components/intercom';
import LogRocket from 'logrocket';
import { compressToUTF16, decompress, decompressFromUTF16 } from 'lz-string';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import track from 'react-tracking';
import { ReduxState } from 'types';
import { retryOnRecoverableErrors } from 'utils/api/axiosRetry';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import { getErrorStrategyForAuthorizationMessage } from 'utils/util/error';
import { processRipplingData } from 'utils/util/MergeRipplingUtils';
import Layout from '../../app/src/views/Layout/Layout';
import { loadApp, setAuthUser } from './actions';
import {
  IDENTIFIER_FIRST_LOGIN,
  INVALID_STATE,
  ONE_SECOND_IN_MILLISECONDS,
  TIME_ERROR_MESSAGE_INCIPIT,
  UNAUTHED_REDIRECT_KEY,
  URL_QUERY_PARAMETERS,
  VERIFY_EMAIL_ERROR_MESSAGE,
} from './consts/consts';
import { mokeyPatchForTranslationPluginErrorMessage } from './hacks/hacks';
import { useConfirmIntl } from './locale/ConfirmIntlContext';
import { AUTH0_PARAMS } from './utils/api/AuthProvider';
import ConfirmAPI from './utils/api/ConfirmAPI';
import ElasticsearchAPI from './utils/api/ElasticsearchAPI';
import {
  getActiveAuthProvider,
  isKeycloakEnabled,
} from './utils/api/keycloakClient';
import { navigationService } from './utils/api/navigationService';
import {
  getCustomSessionTTL,
  hasCustomSessionTTL,
  ORGANIZATION_SUSPENDED_CODE,
} from './utils/models/Organization';
import {
  getShouldShowForcedAuthModal,
  reverseLocalStorageFullKey,
  SHOULD_SHOW_FORCED_AUTH_MODAL_KEY,
  setHasSeenSelectAccountPrompt,
  setShouldShowForcedAuthModal,
} from './utils/models/User';
import config from './utils/util/config';
import {
  getTwoNamesForLshus,
  getTwoNamesForUsana,
  getTwoNamesFromName,
  log,
} from './utils/util/util';
import { redirectLoginOptionsGenerator } from './utils/util/utiltsx';
import Analytics from './views/Layout/Analytics';
import PageMaintenance from './views/Layout/Pages/Errors/PageMaintenance';
import PageSuspendedOrganization from './views/Layout/Pages/Errors/PageSuspendedOrganization';
import PageUnprovisioned from './views/Login/PageUnprovisionedUser';
import Loading from './views/Widgets/Loading';
import ForcedAuthModal from './views/Widgets/Modals/ForcedAuthModal';

// @ts-expect-error
const tracking = config.getTracking();
// @ts-expect-error
const isIntercomEnabled = config.isIntercomEnabled();

// This is different than the standard localStorage key generator
// since it is used for un-authed users
const getLocalStorageFullKey = (key) => {
  return (
    '@@confirm@@::' + process.env.REACT_APP_VERSION + '::UnAuthorized::' + key
  );
};

mokeyPatchForTranslationPluginErrorMessage();

const App = (props) => {
  const {
    isLoading,
    isAuthenticated,
    error,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
    getIdTokenClaims,
  } = useAuthProvider();

  // set props to local variables for useEffect
  const setAuthUser = props.setAuthUser;
  const loadApp = props.loadApp;
  const history = useHistory();
  const location = useLocation();
  const redirect_key = getLocalStorageFullKey(UNAUTHED_REDIRECT_KEY);
  const { finalLocale, addRegisteredInterceptor, addLanguageApiInterceptor } =
    useConfirmIntl();
  const { formatMessage } = useIntl();

  const [appIsInitialized, setAppIsInitialized] = useState(false);
  const [customSessionTTLIsInitialized, setCustomSessionTTLIsInitialized] =
    useState(false);
  const [localShouldShowForcedAuthModal, setLocalShouldShowForcedAuthModal] =
    useState(undefined);
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [isUnprovisioned, setIsUnprovisioned] = useState(false);
  // undefined while the organization is not known to be on hold; once the
  // API locks us out this holds the custom message (or null for the default)
  const [suspendedOrganizationMessage, setSuspendedOrganizationMessage] =
    useState<string | null | undefined>(undefined);

  // we use useRef here so we don't pass the location into the effects below,
  // which would cause them to re-run every time the location changed
  const isRefreshingToken = useRef(false);
  const currentLocation = useRef(null);
  useEffect(() => {
    // @ts-expect-error
    currentLocation.current = location;
  }, [location]);

  // Guards the `!isAuthenticated` redirect below (history.replace/
  // loginWithRedirect) so it only fires once per unauthenticated episode.
  // Without this, since that redirect runs directly in the render body
  // (not an effect), history.replace() - unlike a real provider redirect,
  // which navigates the browser away entirely - notifies Router listeners
  // on every call even to an unchanged location (confirmed in history's
  // source), triggering another render, which (isAuthenticated still
  // false) calls it again: an unbounded loop that pegs the CPU. Reset once
  // authentication succeeds so a later logout can redirect again.
  const hasHandledUnauthenticated = useRef(false);
  useEffect(() => {
    if (isAuthenticated) {
      hasHandledUnauthenticated.current = false;
    }
  }, [isAuthenticated]);

  // Only actually used as a fallback when Keycloak/the identifier-first
  // login page is disabled (see isKeycloakEnabled() below) - otherwise
  // IdentifierFirstLogin.tsx derives its own from the `continue` URL param.
  const isSignup = location.pathname === '/signup';

  // Event listener used to clear the modal to force login once
  // the external window is closed post Auth.
  window.addEventListener('storage', (event) => {
    // Event listener on local storage, this is only triggered if *another* browser
    // tab/window changes the local storage value in the `event`.
    const key = event.key;
    if (!key) {
      // If there is no key it's pointless to continue, we know we need an event
      // with key SHOULD_SHOW_FORCED_AUTH_MODAL_KEY.
      return;
    }

    const localStorageFullKey = reverseLocalStorageFullKey(key);
    if (
      localStorageFullKey &&
      localStorageFullKey.endsWith(SHOULD_SHOW_FORCED_AUTH_MODAL_KEY)
    ) {
      if (event.newValue === null) {
        // We udpate the current state of App, localStorage has already been updated
        // by the external popup window since we got here in this EventListener.
        // @ts-expect-error
        setLocalShouldShowForcedAuthModal(false);
      }
    }
  });

  // Copy the value for re-login from localstorage into the current component.
  // Once, at first render.
  useEffect(() => {
    // Force a boolean.
    // @ts-expect-error
    setLocalShouldShowForcedAuthModal(!!getShouldShowForcedAuthModal());
  }, []);

  const checkIfMaintanence = useCallback((error) => {
    // this is a special set the load balancer to respond 503 with an additional marker
    // response body  {'maintenance': true} when we are in maintenance mode.
    // Added a body to distinguish between a normal 503 and our maintenance mode.
    if (error?.response?.status === 503 && error?.response?.data?.maintenance) {
      setIsMaintenance(true);
    }
  }, []);

  const checkIfUnprovisioned = useCallback((error) => {
    if (
      error?.response?.status === 403 &&
      error?.response?.data?.code === 'not_provisioned'
    ) {
      setIsUnprovisioned(true);
    }
  }, []);

  const handleVerifyEmailRetry = useCallback(() => {
    loginWithRedirect();
  }, [loginWithRedirect]);

  const checkIfSuspendedOrganization = useCallback((error) => {
    if (
      error?.response?.status === 403 &&
      error?.response?.data?.code === ORGANIZATION_SUSPENDED_CODE
    ) {
      setSuspendedOrganizationMessage(
        error.response.data.suspended_message ?? null
      );
    }
  }, []);

  useEffect(() => {
    if (!(isAuthenticated && appIsInitialized)) {
      return;
    }

    [ConfirmAPI, ElasticsearchAPI].forEach((api) => {
      api.interceptors.response.use(
        async (res) => {
          // ----------------------------------------
          // Custom code for early token refresh.
          // This is run in a response interceptor to give the request an opportunity
          // to complete sucessfully before forcing a re-login and exchanging a new token.
          // If there are other competing requests (i.e. typeaheads) on the page the first
          // response over SessionTTL will force a logout making any other request possibly fail.
          // More info in utils/util/CustomSessionTTL.js.
          if (!(props.currentOrganization && !customSessionTTLIsInitialized)) {
            return res;
          }

          try {
            if (hasCustomSessionTTL(props.currentOrganization)) {
              // This is needed to ensure we have the claims.
              await getAccessTokenSilently(AUTH0_PARAMS);

              const claims = await getIdTokenClaims();
              if (!claims || 'auth_time' in claims === false) {
                console.error(
                  'No claims found or missing auth_time, skipping early token refresh.' +
                    ' We MUST have claims here.'
                );
                return res;
              }

              const now = Math.floor(Date.now() / ONE_SECOND_IN_MILLISECONDS);
              const customSessionTTL = getCustomSessionTTL(
                props.currentOrganization
              );
              if (now - claims?.auth_time > customSessionTTL) {
                setHasSeenSelectAccountPrompt(false);
                // We need to keep these 2 in lockstep.
                // One is local used to trigger a react re-render.
                // The other is in localstorage for the popup window to use.
                // @ts-expect-error
                setLocalShouldShowForcedAuthModal(true);
                setShouldShowForcedAuthModal(true);
              }
            }
          } catch (e) {
            console.error(e);
          }

          return res;
        },
        (error) => {
          checkIfMaintanence(error);
          checkIfUnprovisioned(error);
          checkIfSuspendedOrganization(error);
          return retryOnRecoverableErrors(api, error);
        }
      );
    });

    if (props.currentOrganization && !customSessionTTLIsInitialized) {
      setCustomSessionTTLIsInitialized(true);
    }
  }, [
    appIsInitialized,
    customSessionTTLIsInitialized,
    getAccessTokenSilently,
    getIdTokenClaims,
    isAuthenticated,
    localShouldShowForcedAuthModal, // Trigger a re-run if this changes.
    props.currentOrganization,
    checkIfMaintanence,
    checkIfUnprovisioned,
    checkIfSuspendedOrganization,
  ]);

  useEffect(() => {
    // don't do anything else until we've authenticated and the
    // app is NOT already initialized
    if (
      appIsInitialized ||
      !isAuthenticated ||
      !getAccessTokenSilently ||
      !loginWithRedirect ||
      !setAuthUser ||
      !user
    ) {
      return;
    }

    log('Authenticated as Auth0 user: ');
    log(user);

    // don't ask for which account when auto-refreshing session and checking if still
    // logged into third party used to authenticate
    setHasSeenSelectAccountPrompt(true);

    // NOTE: the below should be called ONLY ONCE for the given session (hence the async use and
    // checking appIsInitialized
    // set ConfirmAPI so it always gets token silently (which shows auth screen if token has expired)
    // (note that it will NOT send a request to auth0 if the token has not yet expired
    [ConfirmAPI, ElasticsearchAPI].forEach((api) => {
      api.interceptors.request.use(
        async (req) => {
          let token = null;
          try {
            // @ts-expect-error
            token = await getAccessTokenSilently(AUTH0_PARAMS);
          } catch (e) {
            console.info(e);
            // keycloak-js's updateToken() (see KeycloakAuthContext.tsx) never
            // rejects with an Auth0-shaped {error: '...'} - it only rejects
            // when there's no way to get a fresh token (no refresh token, or
            // the token endpoint refused it), which always means "must log
            // in again" - so unlike Auth0, where other rejection reasons
            // exist that shouldn't force a re-login, any failure here is
            // reason enough while Keycloak is the active provider.
            if (
              (isKeycloakEnabled() && getActiveAuthProvider() === 'keycloak') ||
              // @ts-expect-error
              e.error === 'login_required' ||
              // @ts-expect-error
              e.error === 'consent_required' ||
              // @ts-expect-error
              e.message?.startsWith(TIME_ERROR_MESSAGE_INCIPIT)
            ) {
              // We need to clear state if we are re-auth, a user could have
              // interrupted the re-auth process and have invalid state.
              // @ts-expect-error
              setLocalShouldShowForcedAuthModal(false);
              setShouldShowForcedAuthModal(false);

              // Send the user through our own identifier-first login page
              // rather than calling loginWithRedirect directly here - same
              // reasoning as the equivalent gates in ProtectedRoute.tsx and
              // this file's own top-level `if (!isAuthenticated)` check
              // below: picking a provider is the backend's call via
              // /api/auth/resolve-provider, not whichever one happened to
              // be active before this token refresh failed. Unless that
              // page/Keycloak is disabled, in which case fall back to
              // going straight to Auth0 (the pre-Keycloak behavior).
              const loc: any = currentLocation.current;
              const continueUrl =
                loc.pathname + (loc.search || '') + (loc.hash || '');
              if (isKeycloakEnabled()) {
                history.replace(
                  IDENTIFIER_FIRST_LOGIN.path +
                    '?' +
                    URL_QUERY_PARAMETERS.CONTINUE_URL +
                    '=' +
                    encodeURIComponent(continueUrl)
                );
              } else {
                await loginWithRedirect(
                  redirectLoginOptionsGenerator(continueUrl, isSignup)
                );
              }
            }
            isRefreshingToken.current = true;
            throw e;
          }

          isRefreshingToken.current = false;
          req.headers.Authorization = 'Bearer ' + token;
          return req;
        },
        (error) => Promise.reject(error)
      );

      // register the interceptor so we can remove it later
      // when switching language in the app
      addRegisteredInterceptor(addLanguageApiInterceptor(api, finalLocale));
    });

    // note: Apple uses first_name / last_name instead of given_name / family_name,
    // so change it here so we have a standard user throughout the app
    const userToSave = user;
    if (userToSave.first_name) {
      userToSave.given_name = userToSave.first_name;
    }
    if (userToSave.last_name) {
      userToSave.family_name = userToSave.last_name;
    }

    // if no given/family name passed in, parse it out
    // (and should match how auth0backend.py does it on the backend, so update BOTH if you update this)
    if (userToSave.name && !userToSave.given_name && !userToSave.family_name) {
      // [HOTFIX] For LSUHS we throw here an 80/20 to split the name
      // based on what we are getting from Microsoft for them.
      if (
        user.email &&
        (user.email.endsWith('lsuhs.edu') ||
          user.email.endsWith('fabuwood.com') ||
          user.email.endsWith('zgf.com'))
      ) {
        const names = getTwoNamesForLshus(userToSave.name);
        userToSave.given_name = names[0];
        userToSave.family_name = names[1];
      } else if (user.email && user.email.endsWith('usanainc.com')) {
        const names = getTwoNamesForUsana(userToSave.name);
        userToSave.given_name = names[0];
        userToSave.family_name = names[1];
      } else {
        // parse it manually; this is the case as of 4/2020 for Apple on Auth0
        // not passing in the proper thing (this parses "David Isaac Murray" into "David Isaac" first and "Murray" last)
        const names = getTwoNamesFromName(userToSave.name);
        userToSave.given_name = names[0];
        userToSave.family_name = names[1];
      }
    }

    // fetch user profile and set app to initialized
    setAuthUser(userToSave);
    setAppIsInitialized(true);

    // remove redirect URL upon successful navigation
    localStorage.removeItem(redirect_key);

    log('App initialized.');
  }, [
    appIsInitialized,
    getAccessTokenSilently,
    isAuthenticated,
    loginWithRedirect,
    setAuthUser,
    user,
    redirect_key,
    finalLocale,
    addRegisteredInterceptor,
    addLanguageApiInterceptor,
    isSignup,
  ]);

  const ripplingData = processRipplingData(location.search);

  // We should re-initialize the app ONLY if a new proxy user is set
  // (otherwise, it should only call once for a give auth0 user even
  // if their authentication token expires/renews)
  useEffect(() => {
    if (appIsInitialized) {
      log(
        `Sending /init-app request for ${
          props.currentProxyPerson?.email ?? 'authenticated user'
        } - proxy organization ${props.currentProxyOrganization?.id ?? 'none'}`
      );
      loadApp(
        props.currentProxyPerson?.email,
        props.currentProxyOrganization?.id,
        !!ripplingData,
        (_returnData, error) => checkIfMaintanence(error)
      );
    }
  }, [
    appIsInitialized,
    checkIfMaintanence,
    ripplingData,
    loadApp,
    props.currentProxyPerson,
    props.currentProxyOrganization,
  ]);

  const isReload =
    new URLSearchParams(location.search).get('reloaded') === 'true';

  useEffect(() => {
    if (!(location && !isReload && props.isUnableToLoadDueToError)) {
      return;
    }

    // wait 3 seconds and do a FULL page reload (so we also pick up a fresh
    // bundle) as the error may be related to a push, and append a
    // reloaded=true query parameter so we don't do this indefinitely; if the
    // reloaded page fails again, the error page is shown (see render below).
    // While waiting, the app renders a spinner rather than the app itself,
    // which would otherwise run with missing or stale data.
    const timeout = setTimeout(() => {
      const searchObj = new URLSearchParams(location.search);
      searchObj.set('reloaded', 'true');
      navigationService.assign(
        location.pathname + '?' + searchObj.toString() + (location.hash || '')
      );
    }, 3000);

    return () => clearTimeout(timeout);
  }, [location, isReload, props.isUnableToLoadDueToError]);

  if (isMaintenance) {
    // if ?maintenance=true is in url, show maintenance page
    // (without header). This is needed to allow AWS Amplify to
    // use a url param as a way to show the maintenance page)
    return <PageMaintenance />;
  }

  if (isUnprovisioned) {
    return <PageUnprovisioned />;
  }

  if (suspendedOrganizationMessage !== undefined) {
    return (
      <PageSuspendedOrganization
        suspendedMessage={suspendedOrganizationMessage}
      />
    );
  }

  if (error) {
    if (error.message === INVALID_STATE && !isReload) {
      console.warn('Retrying due to authentication error: ' + error.message);

      // Update the query string to add reload, and remove auth information
      const searchObj = new URLSearchParams(location.search);
      searchObj.set('reloaded', 'true');
      searchObj.delete('code');
      searchObj.delete('state');

      // Need to use browser redirect to trigger new auth process
      // NOTE: redirect with react-router insufficient
      const redirectURL =
        window.location.origin + location.pathname + '?' + searchObj.toString();
      // @ts-expect-error
      window.location = redirectURL;

      return;
    } else {
      // remove redirect URL if auth process fails
      localStorage.removeItem(redirect_key);

      if (error.message === VERIFY_EMAIL_ERROR_MESSAGE) {
        const toggleSupport = () => {
          showSupportChat();
        };

        const errorMessage = (
          <>
            <FormattedMessage
              id="app.app.verify_your_account"
              defaultMessage="Click 'Verify your account' in the email you just received to <span>log in</span>."
              values={{
                span: (chunks) => (
                  <span
                    className="text-primary"
                    role="button"
                    onClick={handleVerifyEmailRetry}
                  >
                    {chunks}
                  </span>
                ),
              }}
            />
            <br />
            <br />
            {isIntercomEnabled ? (
              <FormattedMessage
                id="app.app.check_spam"
                defaultMessage="If you don't see it, check your spam folder. Still having trouble? <span>Let us know</span>"
                values={{
                  span: (chunks) => (
                    <span
                      className="text-primary"
                      role="button"
                      onClick={toggleSupport}
                    >
                      {chunks}
                    </span>
                  ),
                }}
              />
            ) : (
              <FormattedMessage
                id="app.app.check_spam_without_support"
                defaultMessage="If you don't see it, check your spam folder."
                description="Shown after a verification email is sent, when no support chat is available"
              />
            )}
          </>
        );

        const errorHeading = (
          <FormattedMessage
            id="app.app.check_your_email"
            defaultMessage="Check your email"
          />
        );

        return (
          // @ts-expect-error
          <Layout errorHeading={errorHeading} errorMessage={errorMessage} />
        );
      } else if (error.message?.startsWith(TIME_ERROR_MESSAGE_INCIPIT)) {
        const toggleSupport = () => {
          showSupportChat();
        };

        const errorMessage = (
          <>
            <FormattedMessage
              id="app.app.manually_set_computer_time"
              defaultMessage="
            Have you manually set your computer time? If so, please sync your
            machine clocks with the OS automatic time settings.
            "
            />
            {isIntercomEnabled && (
              <>
                <br />
                <br />
                <FormattedMessage
                  id="app.app.still_having_trouble"
                  defaultMessage="Still having trouble? <span>Let us know</span>"
                  values={{
                    span: (chunks) => (
                      <span
                        className="text-primary"
                        role="button"
                        onClick={toggleSupport}
                      >
                        {chunks}
                      </span>
                    ),
                  }}
                />
              </>
            )}
          </>
        );

        const errorHeading = (
          <FormattedMessage
            id="app.app.invalid_computer_date_time_detected"
            defaultMessage="Invalid computer date/time detected."
          />
        );

        return (
          // @ts-expect-error
          <Layout errorHeading={errorHeading} errorMessage={errorMessage} />
        );
      }

      const errorStrategy = getErrorStrategyForAuthorizationMessage(
        error.message,
        formatMessage
      );

      switch (errorStrategy.action) {
        case 'DISPLAY_USER_ONLY':
          console.warn('Authentication error: ' + errorStrategy.errorMessage);
          return <Layout errorMessage={errorStrategy.errorMessage} />;
        case 'DISPLAY_SENTRY_AND_USER':
          console.error('Authentication error: ' + errorStrategy.errorMessage);
          return <Layout errorMessage={errorStrategy.errorMessage} />;
      }
    }
  }

  if (props.isUnableToLoadDueToError) {
    if (isReload) {
      console.error('App is unable to load due to backend error.');

      const errorMessage = (
        <FormattedMessage
          id="app.app.error_loading_account"
          defaultMessage="Please wait a moment and try again. If this continues, contact customer support."
        />
      );

      return <Layout errorMessage={errorMessage} />;
    }

    // a full page reload is about to be triggered (see the effect above);
    // never render the app in the meantime, as init-app failed and the app
    // would run with missing data (or stale data from local storage)
    return (
      <Loading
        message={formatMessage({
          id: 'app.app.loading_your_data',
          defaultMessage: 'Loading your data...',
        })}
      />
    );
  }

  if (isLoading || isRefreshingToken.current) {
    return (
      <Loading
        message={formatMessage({
          id: 'app.app.message.initializing',
          defaultMessage: 'Initializing...',
        })}
      />
    );
  }

  if (!isAuthenticated) {
    log('Authenticating...');
    let destination = location;

    // Check for previously stored redirect URL
    const redirectLocation = localStorage.getItem(redirect_key);

    if (redirectLocation) {
      try {
        destination = JSON.parse(decompressFromUTF16(redirectLocation));
      } catch (e) {
        // presumably because item is stored with old (non-UTF16) format,
        // fallback to former decompression method
        destination = JSON.parse(decompress(redirectLocation));
      }
    } else {
      // adding redirect to localStorage to be used later (if needed)
      localStorage.setItem(
        redirect_key,
        compressToUTF16(JSON.stringify(location)) // use UTF16 version so it works on FF / IE
      );
    }

    // We need to skip auto-login if we are getting here via ForcedAuthModal
    // logout's call to let Auth0 show the login prompt.
    if (!localShouldShowForcedAuthModal) {
      // We need to clear state if we are re-auth, a user could have
      // interrupted the re-auth process and have invalid state.
      if (localShouldShowForcedAuthModal !== false) {
        // Strict boolean check to avoid infinite re-renderings.
        // @ts-expect-error
        setLocalShouldShowForcedAuthModal(false);
      }
      setShouldShowForcedAuthModal(false);

      // Guarded by hasHandledUnauthenticated (see its declaration above) -
      // history.replace()/loginWithRedirect() must only fire once per
      // unauthenticated episode, not on every render.
      if (!hasHandledUnauthenticated.current) {
        hasHandledUnauthenticated.current = true;

        // Send the user through our own identifier-first login page to
        // (re-)pick the right provider by email, rather than redirecting
        // straight to whichever provider was last used (see
        // ProtectedRoute.tsx, which does the same for protected routes -
        // this is the equivalent gate for App.tsx's own top-level check).
        // Unless that page/Keycloak is disabled, in which case fall back to
        // going straight to Auth0 (the pre-Keycloak behavior).
        const continueUrl =
          destination.pathname +
          (destination.search || '') +
          (destination.hash || '');
        if (isKeycloakEnabled()) {
          console.debug('[app][call] redirect to identifier-first login');
          history.replace(
            IDENTIFIER_FIRST_LOGIN.path +
              '?' +
              URL_QUERY_PARAMETERS.CONTINUE_URL +
              '=' +
              encodeURIComponent(continueUrl)
          );
        } else {
          console.debug('[app][call] loginWithRedirect');
          loginWithRedirect(
            redirectLoginOptionsGenerator(continueUrl, isSignup)
          );
        }
      }
    }
  }

  if (!appIsInitialized || props.isLoadingApp) {
    return (
      <Loading
        message={formatMessage({
          id: 'app.app.loading_your_data',
          defaultMessage: 'Loading your data...',
        })}
      />
    );
  }

  // Below means we are logged in or have an error

  if (ripplingData && window.location.pathname !== '/integrations') {
    // We need to ensure to redirect to the integrations page
    // before any other redirect (Welcome, Campaign, ...).

    // Re-build original querystring.
    const params = new URLSearchParams();
    params.append('code', ripplingData.code);
    params.append('redirect_uri', ripplingData.redirect_uri);
    window.location.href = '/integrations?' + params.toString();
    return (
      <Loading
        message={formatMessage({
          id: 'app.app.message.initializing',
          defaultMessage: 'Initializing...',
        })}
      />
    );
  }

  return (
    <>
      <ForcedAuthModal
        toggle={() => {
          // @ts-expect-error
          setLocalShouldShowForcedAuthModal(!localShouldShowForcedAuthModal);
        }}
        isOpen={!!localShouldShowForcedAuthModal}
        // @ts-expect-error
        returnTo={currentLocation.current}
        ttl={
          props.currentOrganization
            ? getCustomSessionTTL(props.currentOrganization)
            : undefined
        }
      />
      {<Analytics />}
      {<Layout />}
    </>
  );
};

App.propTypes = {
  setAuthUser: PropTypes.func.isRequired,
  loadApp: PropTypes.func.isRequired,
  isUnableToLoadDueToError: PropTypes.bool,
  isLoadingApp: PropTypes.bool,
  currentProxyPerson: PropTypes.object,
};

// These props come from the application's
// state when it is started
const mapStateToProps = (state: ReduxState) => {
  const {
    isUnableToLoadDueToError,
    isLoadingApp,
    currentProxyPerson,
    currentOrganization,
    currentProxyOrganization,
  } = state;

  return {
    isUnableToLoadDueToError,
    isLoadingApp,
    currentProxyPerson,
    currentProxyOrganization,
    currentOrganization,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setAuthUser: (user) => dispatch(setAuthUser(user)),
    loadApp: (proxy, proxyOrg, isInRipplingInstallFlow, callback) =>
      dispatch(loadApp(proxy, proxyOrg, isInRipplingInstallFlow, callback)),
  };
};

// all tracking in app will be passed through here
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  track(
    {},
    {
      dispatch: (data) => {
        // NOTE: conventions for data tracking object:
        // - action: string
        // - details: JSON object

        if (tracking.useLogRocketEventTracking) {
          LogRocket.track(data.action, data.metadata);
        }

        if (tracking.useGoogleAnalyticsTracking) {
          const ev = {
            category: 'app',
            action: data.action,
          };

          if (data.metadata) {
            if (typeof data.metadata['label'] === 'undefined') {
              ev['label'] = JSON.stringify(data.metadata);
            } else {
              ev['label'] = data.metadata['label'];
            }
          }

          ReactGA.event(ev);
        }

        if (tracking.useConsoleEventTracking) {
          console.log(data);
        }
      },
    }
    // @ts-expect-error
  )(React.memo(App))
);
